import { useLocation } from 'react-router-dom';

import { CustomCursor } from '@/components/ui';
import { ImpersonationBanner } from '@/features/admin/ImpersonationBanner';
import { useAuth } from '@/hooks/useAuth';
import { AppRoutes } from '@/routes';
import { paths } from '@/routes/paths';

import { AppProviders } from './providers';

/**
 * Where the native pointer is kept: the signed-in portals, and the sign-in,
 * registration and password screens that lead into them.
 */
const NATIVE_CURSOR_ROOTS = [
  paths.student.root,
  paths.company.root,
  paths.admin.root,
  paths.login,
  '/register',
  paths.verifyEmail,
  paths.forgotPassword,
  paths.resetPassword,
];

function keepsNativeCursor(pathname) {
  return NATIVE_CURSOR_ROOTS.some((root) => pathname === root || pathname.startsWith(`${root}/`));
}

/**
 * The custom cursor is for visitors only: the marketing pages, signed out.
 * Once someone is signed in the product is a working tool everywhere --
 * the public internship list included -- and the native pointer, with its
 * text, resize and not-allowed shapes, reads better. The sign-in and
 * registration forms keep it native too. Unmounting restores the native
 * cursor (see CustomCursor's cleanup).
 *
 * Nothing is shown while the session is still being checked, so a signed-in
 * user never sees the custom cursor flash on before it disappears.
 */
function PublicCursor() {
  const { pathname } = useLocation();
  const { status, isAuthenticated } = useAuth();
  if (status === 'initialising' || isAuthenticated) return null;
  return keepsNativeCursor(pathname) ? null : <CustomCursor />;
}

export function App() {
  return (
    <AppProviders>
      <ImpersonationBanner />
      <AppRoutes />
      <PublicCursor />
    </AppProviders>
  );
}
